/**
 * @fileoverview Assignment Status Tracking Hook
 * 
 * Provides simple status tracking for knowledge base assignments
 * using standard HTTP requests.
 * 
 * Requirements: 4.6, 7.5, 7.6
 */

import { useCallback, useState } from 'react';

/**
 * Hook for tracking assignment status with simple HTTP requests
 * 
 * @param {KnowledgeBaseAssignmentConfig} config - Module configuration
 * @param {ModuleContext} moduleContext - Module context
 * @returns {Object} Assignment status utilities
 */
export function useSyncStatusTracking(config, moduleContext) {
  const [assignmentStatuses, setAssignmentStatuses] = useState({});
  const [loading, setLoading] = useState(false);

  // Update assignment status
  const updateAssignmentStatus = useCallback(async (fileId, parrotId, action) => {
    const key = `${fileId}:${parrotId}`;

    try {
      setLoading(true);
      setAssignmentStatuses(prev => ({
        ...prev,
        [key]: { status: 'updating', lastUpdated: Date.now() }
      }));

      const response = await moduleContext.apiClient.post('/api/knowledge-base/assignments', {
        fileId,
        parrotId,
        action, // 'assign' or 'unassign'
        context: config.context
      });

      setAssignmentStatuses(prev => ({
        ...prev,
        [key]: {
          status: response.success ? 'completed' : 'failed',
          result: response,
          lastUpdated: Date.now()
        }
      }));

      return response;
    } catch (error) {
      setAssignmentStatuses(prev => ({
        ...prev,
        [key]: {
          status: 'failed',
          error: error.message,
          lastUpdated: Date.now()
        }
      }));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [config.context, moduleContext.apiClient]);

  // Get assignment status
  const getStatus = useCallback((fileId, parrotId) => {
    const key = `${fileId}:${parrotId}`;
    return assignmentStatuses[key] || null;
  }, [assignmentStatuses]);

  // Clear status for a specific assignment
  const clearStatus = useCallback((fileId, parrotId) => {
    const key = `${fileId}:${parrotId}`;
    setAssignmentStatuses(prev => {
      const newStatuses = { ...prev };
      delete newStatuses[key];
      return newStatuses;
    });
  }, []);

  // Clear all statuses
  const clearAllStatuses = useCallback(() => {
    setAssignmentStatuses({});
  }, []);

  // Legacy compatibility methods (for existing code that might use these)
  const initialize = useCallback(() => {
    // No-op for compatibility
  }, []);

  const subscribe = useCallback(() => {
    // No-op for compatibility
  }, []);

  const unsubscribe = useCallback(() => {
    // No-op for compatibility
  }, []);

  const getFileStatuses = useCallback((fileId) => {
    const statuses = {};
    Object.entries(assignmentStatuses).forEach(([key, status]) => {
      if (key.startsWith(`${fileId}:`)) {
        const parrotId = key.split(':')[1];
        statuses[parrotId] = status;
      }
    });
    return statuses;
  }, [assignmentStatuses]);

  const getConnectionHealth = useCallback(() => {
    return {
      connected: true,
      healthy: true,
      lastHeartbeat: new Date(),
      error: null,
      usingPolling: false,
      state: 'connected',
      reconnectAttempts: 0
    };
  }, []);

  return {
    // New simple API
    updateAssignmentStatus,
    getStatus,
    clearStatus,
    clearAllStatuses,
    loading,
    assignmentStatuses,

    // Legacy compatibility
    initialize,
    subscribe,
    unsubscribe,
    getFileStatuses,
    getConnectionHealth,
    connectionState: { connected: true, state: 'connected' },
    syncStatuses: assignmentStatuses
  };
}

export default useSyncStatusTracking;